import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  Snackbar,
  Alert,
} from '@mui/material';
import BlockIcon from '@mui/icons-material/Block';
import RefreshIcon from '@mui/icons-material/Refresh';
import KeyIcon from '@mui/icons-material/Key';
import EditIcon from '@mui/icons-material/Edit';
import {
  listApiClients,
  listApiScopes,
  revokeApiClient,
  rotateApiClientSecret,
  ApiClientListItem,
  ApiScopeGroup,
} from '../services/apiClientService';
import CreateApiClientModal from './CreateApiClientModal';
import EditApiClientScopesModal from './EditApiClientScopesModal';

export default function AdminApiClientsTab() {
  const [items, setItems] = useState<ApiClientListItem[]>([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [rotatedSecret, setRotatedSecret] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [tree, setTree] = useState<ApiScopeGroup[]>([]);
  const [editing, setEditing] = useState<ApiClientListItem | null>(null);

  useEffect(() => {
    listApiScopes().then(setTree).catch(() => setTree([]));
  }, []);

  const scopeLabel = (s: string): string => {
    for (const grp of tree) {
      const leaf = grp.children.find((c) => c.scope === s);
      if (leaf) return leaf.label;
    }
    return s;
  };

  const load = useCallback(async () => {
    try {
      setItems(await listApiClients());
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Load failed');
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const onRevoke = async (id: string, name: string) => {
    if (!window.confirm(`Revoke API client "${name}"? This cannot be undone.`)) return;
    try {
      await revokeApiClient(id);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Revoke failed');
    }
  };

  const onRotate = async (id: string, name: string) => {
    if (
      !window.confirm(
        `Rotate the client_secret for "${name}"? The old secret stops working immediately.`
      )
    )
      return;
    try {
      const r = await rotateApiClientSecret(id);
      setRotatedSecret(r.client_secret);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Rotate failed');
    }
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" mb={2}>
        <Button startIcon={<RefreshIcon />} onClick={load}>
          Refresh
        </Button>
        <Button variant="contained" onClick={() => setCreateOpen(true)}>
          New API Client
        </Button>
      </Box>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>client_id</TableCell>
              <TableCell>Scopes</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Last used</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((c) => (
              <TableRow key={c.id} hover>
                <TableCell>{c.name}</TableCell>
                <TableCell sx={{ fontFamily: 'monospace' }}>{c.client_id}</TableCell>
                <TableCell>
                  {c.scopes.map((s) => (
                    <Chip key={s} label={scopeLabel(s)} size="small" sx={{ mr: 0.5 }} title={s} />
                  ))}
                </TableCell>
                <TableCell>
                  {c.is_active ? (
                    <Chip label="active" color="success" size="small" />
                  ) : (
                    <Chip label="revoked" color="default" size="small" />
                  )}
                </TableCell>
                <TableCell>
                  {c.last_used_at ? new Date(c.last_used_at).toLocaleString() : '—'}
                </TableCell>
                <TableCell align="right">
                  <Tooltip title="Edit client (name + scopes)">
                    <span>
                      <IconButton
                        size="small"
                        onClick={() => setEditing(c)}
                        disabled={!c.is_active}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Rotate secret">
                    <span>
                      <IconButton
                        size="small"
                        onClick={() => onRotate(c.id, c.name)}
                        disabled={!c.is_active}
                      >
                        <KeyIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Revoke">
                    <span>
                      <IconButton
                        size="small"
                        onClick={() => onRevoke(c.id, c.name)}
                        disabled={!c.is_active}
                      >
                        <BlockIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
            {items.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  No API clients yet — click "New API Client" to create one.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <CreateApiClientModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={load}
      />

      <EditApiClientScopesModal
        open={!!editing}
        client={editing}
        onClose={() => setEditing(null)}
        onSaved={load}
      />

      <Snackbar
        open={!!rotatedSecret}
        autoHideDuration={null}
        onClose={() => setRotatedSecret(null)}
      >
        <Alert
          onClose={() => setRotatedSecret(null)}
          severity="warning"
          sx={{ width: '100%' }}
        >
          New secret (copy now — won't be shown again):
          <Box sx={{ fontFamily: 'monospace', mt: 1, wordBreak: 'break-all' }}>
            {rotatedSecret}
          </Box>
        </Alert>
      </Snackbar>

      <Snackbar open={!!error} autoHideDuration={5000} onClose={() => setError(null)}>
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Snackbar>
    </Box>
  );
}
